// University of California, Santa Barbara: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UCSB's Common Data Set 2025-2026 (Institutional Research, Planning &
//    Assessment). Quirk: UCSB's Institutional Research page links the file
//    as "Common Data Set - 2025-26" but hosts it on Google Drive, so the
//    source link below is that official page, not the Drive file. Read: C1
//    applicants/admits/enrollees for Fall 2025 with the residency table, C8A
//    (no use of SAT or ACT), C21/C22 (no early decision, no early action),
//    G1/G5 2026-2027 costs. UCSB's own Tableau dashboard of freshman
//    applicants, admits and enrolls shows the same Fall 2025 counts
//    (110,187 / 42,093 / 5,081), campus totals only.
//  - admissions.sa.ucsb.edu: Dates & Deadlines (fall 2027 filing period,
//    decision timing) and Freshman Eligibility and Selection (the SAT/ACT
//    note and the college-by-college supplemental rules).
//  - College Scorecard school page, id 110705 (net price by family income;
//    the API was rate-limited, so the figures come from the official page).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UCSB Common Data Set 2025-2026',
        url: 'https://bap.ucsb.edu/institutional-research',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'UCSB Admissions, Dates and Deadlines',
        url: 'https://admissions.sa.ucsb.edu/deadlines',
        read: '2026-09-30',
    },
    selection: {
        label: 'UCSB Admissions, Freshman Eligibility and Selection',
        url: 'https://admissions.sa.ucsb.edu/freshman-eligibility-selection',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110705-University-of-California-Santa-Barbara',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-santa-barbara',
    kbId: 'university_of_california_santa_barbara',
    name: 'University of California, Santa Barbara',
    shortName: 'UCSB',
    location: 'Santa Barbara, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UCSB brand site); decoration only.
    branding: {
        primary: '#003660',
        secondary: '#FEBC11',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_santa_barbara_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 110187,
        admitted: 42093,
        enrolled: 5081,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 74486, admitted: 23923 },
            { group: 'Out-of-state', applied: 17067, admitted: 9302 },
            { group: 'International', applied: 18634, admitted: 8868 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UCSB\'s Common Data Set reports one first-year acceptance rate for the whole campus, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UCSB has no early decision or early action plan. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of UCSB\'s three colleges. Some ask for more than the UC application:',
        rows: [
            { school: 'College of Letters and Science, Dance and Music Performance', requirement: 'An audition. For every other Letters and Science major, your choice of major is not considered and no supplemental application is needed.' },
            { school: 'College of Creative Studies', requirement: 'A supplemental application, reviewed by Creative Studies faculty. Pick a second-choice major outside Creative Studies, so you are still reviewed for it if you are not selected.' },
            { school: 'College of Engineering', requirement: 'Alternate Engineering majors are not considered. To be reviewed for an alternate major, choose one in the College of Letters and Science.' },
        ],
        source: 'selection',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application', deadline: 'November 30', decision: 'Mid-March', binding: false },
        ],
        note: 'One UC application covers every UC campus. You can begin it on August 1 and submit it between October 1 and November 30. If you are admitted, your Statement of Intent to Register is due May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UCSB will not use SAT or ACT scores in admission decisions or scholarship selection, so scores are not considered even if you send them. If you are admitted and enroll, scores may be used for course placement.',
        source: 'selection',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [
            { topic: 'Theater', line: 'From fall 2028, the Theater BFA (Acting emphasis) requires an audition as part of the admission process. Until then, students audition at the end of their first year.', source: 'ucGuide' },
        ],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [14202, 53472] },
            { item: 'Required fees', amounts: [3441, 3441] },
            { item: 'Food and housing (on campus)', amounts: [23974, 23974] },
            { item: 'Books and supplies', amounts: [1478, 1478] },
            { item: 'Transportation', amounts: [1008, 1008] },
            { item: 'Other expenses', amounts: [2579, 2579] },
        ],
        totals: [46682, 85952],
        note: 'First-year tuition and fees, with the Common Data Set\'s figures for a student living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 16807,
            byIncome: [
                { income: '$0 to $30,000', amount: 9523 },
                { income: '$30,001 to $48,000', amount: 10281 },
                { income: '$48,001 to $75,000', amount: 14181 },
                { income: '$75,001 to $110,000', amount: 18954 },
                { income: 'Over $110,000', amount: 36658 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UCSB\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
